import type { Editor } from '@tiptap/react';
import { useEditorState, type EditorStateSnapshot } from '@tiptap/react';
import { useCallback, useMemo } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { HeadingIcon } from '@/components/tiptap-editor/tiptap-icons/heading-icon';

// --- Tiptap UI ---
import {
    canToggle,
    headingIcons,
    isHeadingActive,
    shouldShowButton,
    type Level,
} from '@/components/tiptap-editor/tiptap-ui/heading-button';

/**
 * Configuration for the heading dropdown menu functionality
 */
export interface UseHeadingDropdownMenuConfig {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;
    /**
     * Available heading levels to show in the dropdown
     * @default [1, 2, 3, 4, 5, 6]
     */
    levels?: Level[];
    /**
     * Whether the dropdown should hide when headings are not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
}

/**
 * Gets the currently active heading level from the available levels
 */
export function getActiveHeadingLevel(editor: Editor | null, levels: Level[] = [1, 2, 3, 4, 5, 6]): Level | undefined {
    if (!editor || !editor.isEditable) return undefined;
    return levels.find(level => isHeadingActive(editor, level));
}

/**
 * Custom hook that provides heading dropdown menu functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyHeadingDropdown() {
 *   const {
 *     isVisible,
 *     activeLevel,
 *     isAnyHeadingActive,
 *     canToggle,
 *     levels,
 *   } = useHeadingDropdownMenu()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <DropdownMenu>
 *       // dropdown content
 *     </DropdownMenu>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedHeadingDropdown() {
 *   const {
 *     isVisible,
 *     activeLevel,
 *   } = useHeadingDropdownMenu({
 *     editor: myEditor,
 *     levels: [1, 2, 3],
 *     hideWhenUnavailable: true,
 *   })
 *
 *   // component implementation
 * }
 * ```
 */
const DEFAULT_HEADING_LEVELS: Level[] = [1, 2, 3, 4, 5, 6];

type HeadingDropdownSnapshot = {
    isVisible: boolean;
    activeLevel: Level | undefined;
    isActive: boolean;
    canToggle: boolean;
};

function headingDropdownSnapshotEquality(a: HeadingDropdownSnapshot, b: HeadingDropdownSnapshot | null): boolean {
    return a.isVisible === b?.isVisible && a.activeLevel === b?.activeLevel && a.isActive === b?.isActive && a.canToggle === b?.canToggle;
}

export function useHeadingDropdownMenu(config?: UseHeadingDropdownMenuConfig) {
    const { editor: providedEditor, levels: levelsProp, hideWhenUnavailable = false } = config || {};
    const levels = useMemo(() => levelsProp ?? DEFAULT_HEADING_LEVELS, [levelsProp]);

    const { editor } = useTiptapEditor(providedEditor);

    const dropdownSelector = useCallback(
        (ctx: EditorStateSnapshot<Editor | null>): HeadingDropdownSnapshot => {
            const ed = ctx.editor;
            if (!ed) {
                return { isVisible: false, activeLevel: undefined, isActive: false, canToggle: false };
            }
            return {
                isVisible: shouldShowButton({ editor: ed, hideWhenUnavailable, level: levels }),
                activeLevel: getActiveHeadingLevel(ed, levels),
                isActive: isHeadingActive(ed),
                canToggle: canToggle(ed),
            };
        },
        [levels, hideWhenUnavailable],
    );

    const dropdown = useEditorState({
        editor,
        selector: dropdownSelector,
        equalityFn: headingDropdownSnapshotEquality,
    });

    const isVisible = dropdown?.isVisible ?? false;
    const activeLevel = dropdown?.activeLevel;
    const isActive = dropdown?.isActive ?? false;
    const canToggleState = dropdown?.canToggle ?? false;

    return {
        isVisible,
        activeLevel,
        isActive,
        canToggle: canToggleState,
        levels,
        label: 'Heading',
        Icon: activeLevel ? headingIcons[activeLevel] : HeadingIcon,
    };
}
